import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AsyncPipe, CommonModule } from '@angular/common';
import { AbstractControl, TouchedChangeEvent } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, startWith, Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-validation-message',
    imports: [CommonModule, TranslateModule, AsyncPipe],
    templateUrl: './validation-message.component.html',
    styleUrls: ['./validation-message.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ValidationMessageComponent implements OnInit, OnChanges, OnDestroy {
  @Input({required:true}) control: AbstractControl;

  protected readonly showPatternValidation$ = new BehaviorSubject(false);
  protected readonly controlHasError$ = new BehaviorSubject(false);

  protected readonly errorMessageMap: Record<string, string> = {
    required: 'This field is required',
    email: 'Please enter a valid email address',
    atLeastOne: 'At least one of these fields are required',
    noAffectedSelection: 'No selection of affected traffic has been made',
    replacePlaceIdNotSelected: 'New stop not selected',
    noActiveRoutesSelected: 'No affected lines selected',
    noActiveStopsSelected: 'No affected stops selected',
    stopAreasMismatch: 'stopAreasMismatch',
    invalidTimeOrder: 'End time must be after start time',
    invalidTime: 'Please enter a valid time',
    invalidDate: 'Please enter a valid date',
    futureDate: 'Date cannot be in the future',
    publicationStartAfterTimeSpanStart: 'Publication start time must not be after the deviations start time',
    allChipsHasValue: 'All chips in the template must be populated',
  };

  private readonly _destroy$ = new Subject<void>();
  private validationTimeout: ReturnType<typeof setTimeout>;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['control'] && !changes['control'].isFirstChange()) {
      this._teardown();
      this._subscribe();
    }
  }

  ngOnInit(): void {
    this._subscribe();
  }

  ngOnDestroy(): void {
    this._teardown();
    this._destroy$.complete();
  }

  private _teardown(): void {
    if (this.validationTimeout) {
      clearTimeout(this.validationTimeout);
      this.validationTimeout = undefined;
    }
    this.showPatternValidation$.next(false);
    this.controlHasError$.next(false);
    this._destroy$.next();
  }

  private _subscribe(): void {
    this.control.valueChanges.pipe(
      startWith(this.control.value),
      takeUntil(this._destroy$)
    ).subscribe(() => {
      if (this.control.hasError('pattern')) {
        if (this.validationTimeout) clearTimeout(this.validationTimeout);
        
        this.validationTimeout = setTimeout(() => {
          if (this.control.hasError('pattern')) {
            this.showPatternValidation$.next(true);
            this.validationTimeout = undefined;
          }
        }, 1000);
      } else {
        clearTimeout(this.validationTimeout);
        this.validationTimeout = undefined;
        this.showPatternValidation$.next(false);
      }

      // Show error if control has errors AND (is touched or dirty)
      const hasErrors = this.control.errors != undefined;
      const isTouchedOrDirty = this.control.touched || this.control.dirty;
      const shouldShow = hasErrors && isTouchedOrDirty;
      this.controlHasError$.next(shouldShow);
    });

    // Also subscribe to touched and dirty status changes
    this.control.statusChanges.pipe(
      takeUntil(this._destroy$)
    ).subscribe(() => {
      const hasErrors = this.control.errors != undefined;
      const isTouchedOrDirty = this.control.touched || this.control.dirty;
      const shouldShow = hasErrors && isTouchedOrDirty;
      this.controlHasError$.next(shouldShow);
    });

    // React immediately when markAllAsTouched() is called (e.g. on publish)
    this.control.events.pipe(
      takeUntil(this._destroy$)
    ).subscribe(event => {
      if (event instanceof TouchedChangeEvent) {
        const hasErrors = this.control.errors != undefined;
        const isTouchedOrDirty = this.control.touched || this.control.dirty;
        this.controlHasError$.next(hasErrors && isTouchedOrDirty);
      }
    });
  }
}
